import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { BUY } from 'pricing-pages-lib/constants/CtaTypes';
import { getHubsSignupUrl, getPublicSiteDomain } from 'pricing-pages-lib/utils/publicPageUrls';
import { checkoutProductConfigurationsSelector, getFirstHubSkuIdFromProductConfigurations } from 'pricing-pages-ui/selectors/ProductConfigsSelectors';
import { hubsSignupCTASelector, trackingProductNameSelector } from 'pricing-pages-ui/selectors/TrackingSelectors';
import { Metrics } from 'pricing-pages-ui/utils/metrics';
import Raven from 'raven-js';
import { useState } from 'react';
import { DTBOfferTerms } from 'self-service-api/constants/DTBOfferTerms';
import { Flow } from 'signup-constants/Flow';
import { Intent } from 'signup-constants/Intent';
import { useCreateDirectToBuyConfigMutation } from '../../../../__generated__/chirp/com/hubspot/buying/rpc/na1/PublicDirectToBuyRpc.hooks';
import { useGlobalLoading } from '../../../hooks/loadingHooks';
import { useBestApiNameFromProductConfigurations } from '../../../hooks/useBestApiNameFromProductConfigurations';
import { useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { usePurchaseOptionsForProducts } from '../../../hooks/usePurchaseOptions';
import { useTrackingFlow } from '../../../hooks/useTrackingFlow';
import { useAppSelector } from '../../../reducers';
import { getEligiblePublicTreatmentsSelector } from '../../../selectors/ExperimentSelectors';
import { isUngatedForSeatsSelector } from '../../../selectors/GatingSelectors';
import { generateSignupUrlWithQueryParams } from '../../../utils/urls/generateSignupUrl';
import { trackBuyButtonClick } from '../../../v2/utils/tracking/trackingEventUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const DTBButton = ({
  sourceId,
  productConfigurations = [],
  dtbSource,
  use = 'primary',
  nonMerchandiseIdProduct,
  hubsSignupProductOverride,
  queryParamTerm,
  productIdentifier,
  trackingSource,
  children,
  size,
  onClick: passthroughOnClick
}) => {
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const isAppReady = useGlobalLoading();
  const [createDirectToBuyConfig, {
    loading: createDirectToBuyConfigLoading
  }] = useCreateDirectToBuyConfigMutation();
  const currencyCode = useDisplayedCurrencyCode();
  const skuIds = productConfigurations.map(({
    skuId
  }) => skuId);
  const trackingProductNames = useAppSelector(state => productConfigurations.map(({
    skuId
  }) => trackingProductNameSelector(state, {
    skuId
  })));
  const apiName = useBestApiNameFromProductConfigurations(productConfigurations);
  const firstHubSkuId = useAppSelector(state => getFirstHubSkuIdFromProductConfigurations(state, {
    productConfigurations
  }));
  const hubsSignupCta = useAppSelector(state => hubsSignupCTASelector(state, {
    pathname: window.location.pathname,
    skuId: firstHubSkuId || undefined,
    ctaType: BUY,
    sourceId,
    nonMerchandiseIdProduct: hubsSignupProductOverride || nonMerchandiseIdProduct,
    isAppReady
  }));
  const hubsSignupUrl = getHubsSignupUrl({
    nonMerchandiseIdProduct: hubsSignupProductOverride || nonMerchandiseIdProduct
  });
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const checkoutProductConfigurations = useAppSelector(state => checkoutProductConfigurationsSelector(state, {
    productConfigurations
  }));
  const {
    purchaseOptions,
    hasCommonTerms,
    loading: termsLoading
  } = usePurchaseOptionsForProducts({
    productConfigurations: checkoutProductConfigurations,
    term: queryParamTerm
  });
  const eligibleTreatments = useAppSelector(getEligiblePublicTreatmentsSelector);
  const flow = useTrackingFlow({
    productConfigurations,
    purchaseMotion: 'BUY_TOUCHLESS'
  });
  if (termsLoading || !hasCommonTerms || !(purchaseOptions !== null && purchaseOptions !== void 0 && purchaseOptions.length)) {
    return null;
  }
  const selectedOption = purchaseOptions.find(opt => opt.offerInfo.payment === queryParamTerm) || purchaseOptions[0];
  const commitmentTermToUse = selectedOption.offerInfo.commitment;
  const paymentTermToUse = selectedOption.offerInfo.payment;
  const dtbOfferTerm = Object.keys(DTBOfferTerms).find(offerTerm => {
    return DTBOfferTerms[offerTerm].commitment === commitmentTermToUse && DTBOfferTerms[offerTerm].payment === paymentTermToUse;
  });
  const handleClick = e => {
    setFailed(false);
    setLoading(true);
    passthroughOnClick === null || passthroughOnClick === void 0 || passthroughOnClick(e);
    trackBuyButtonClick({
      apiName,
      purchaseMotion: 'BUY_TOUCHLESS',
      productName: apiName,
      productNames: trackingProductNames,
      flow,
      sourceId,
      trackingSource,
      skuIds,
      nonMerchandiseIdProduct,
      hubsSignupUrl,
      hubsSignupCta
    });
    Metrics.counter('buy-now-clicked', {
      source: sourceId || 'DTB button'
    }).increment();
    const domain = getPublicSiteDomain();
    const domainWithoutHttps = domain.replace(/^https:\/\//, '');
    createDirectToBuyConfig({
      variables: {
        offerTerms: [dtbOfferTerm],
        productConfigurations: checkoutProductConfigurations,
        currencyCode
      }
    }).then(response => {
      var _response$data;
      const dtbId = (_response$data = response.data) === null || _response$data === void 0 || (_response$data = _response$data.result) === null || _response$data === void 0 ? void 0 : _response$data.id;
      const href = generateSignupUrlWithQueryParams({
        signupType: Flow.Purchase,
        signupIntent: Intent.purchase,
        queryParams: {
          hubsSignupUrl,
          domainWithoutHttps,
          hubsSignupCta,
          pathname: window.location.pathname,
          isUngatedForSeats,
          dtbId,
          dtbSource,
          currencyCode,
          uuid: window.__utAnonId
        },
        experiments: eligibleTreatments
      });
      window.location.href = href;
    }).catch(error => {
      setFailed(true);
      Raven.captureMessage('Failed to create DTB Config', {
        extra: {
          error,
          skuIds,
          currencyCode,
          queryParamTerm
        }
      });
    }).finally(() => {
      setLoading(false);
    });
  };
  return /*#__PURE__*/_jsx(LoadingButton, {
    "data-selenium-id": `${trackingSource}-${productIdentifier}-BUY_TOUCHLESS-button`,
    use: use,
    onClick: handleClick,
    loading: loading || createDirectToBuyConfigLoading,
    failed: failed,
    block: true,
    margin: "0",
    size: size,
    children: children || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "pricingPage.pricingCtas.buyNow"
    })
  });
};
export default DTBButton;